import {
  NotificationsActiveOutlined,
  NotificationsNoneOutlined,
} from "@mui/icons-material";
import { Paper, Stack, Typography, alpha } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import type { Task } from "fhir/r5";

import { currentFeatures, oids } from "@/configs/Settings";
import { useSessionContext } from "@/contexts/SessionContext";
import { useSidebarNavigation } from "@/contexts/SidebarNavigationContext";
import { useTaskWatchingContext } from "@/contexts/TaskWatchingContext";
import { useFilteredNotifications } from "@/hooks/useFilteredNotifications";
import { PatchTask } from "@/services/fhir/task/PatchTask";
import { getPatientIdForSummaryFromTask } from "@/utils/taskNotificationUtils";
import { getAppointmentReferenceFromQuestionnaireTask } from "src-next/modules/questionnaire-builder/questionnaire-tasks";

import { NotificationsPanelBody } from "@/components/layout/private/main/components/Header/NotificationsPanelBody";

/**
 * Bloco de notificações em destaque no dashboard.
 * Reutiliza os mesmos cards do painel do header.
 */
export function DashboardNotifications() {
  const { user, access } = useSessionContext();
  const { setIsSidebarExpanded } = useSidebarNavigation();
  const { allTasks, updateTaskInContext } = useTaskWatchingContext();
  const navigate = useNavigate();
  const { filteredTasks, unreadCount } = useFilteredNotifications({
    allTasks,
    user,
    access,
  });

  const [updatingById, setUpdatingById] = useState<Record<string, boolean>>({});

  const myPatientReference = useMemo(() => {
    const username = user?.username;
    if (typeof username !== "string" || !/^\d{11}$/.test(username)) {
      return "";
    }
    return `Patient/${oids.cpf}-${username}`;
  }, [user?.username]);

  const handleToggleReadStatus = async (task: Task) => {
    const taskId = task.id;
    if (!taskId) return;
    try {
      setUpdatingById((prev) => ({ ...prev, [taskId]: true }));
      const isCompleted = task?.status === "completed";
      const nextStatus = isCompleted ? "requested" : "completed";

      await PatchTask(taskId, [
        { op: "replace", path: "/status", value: nextStatus },
      ]);
      updateTaskInContext(taskId, { status: nextStatus });
    } catch (error) {
      console.error("Erro ao marcar tarefa como lida:", error);
    } finally {
      setUpdatingById((prev) => ({ ...prev, [taskId]: false }));
    }
  };

  const handleAccessMedicalRecord = async (task: Task) => {
    const taskId = task?.id;
    if (!taskId) return;
    try {
      setUpdatingById((prev) => ({ ...prev, [taskId]: true }));

      const patientId = getPatientIdForSummaryFromTask(task, {
        isDefaultApp: Boolean(currentFeatures.defaultApp),
      });
      if (!patientId) {
        console.warn(
          "Não foi possível derivar patientId para /summary a partir da task",
          task,
        );
        return;
      }

      if (task?.status !== "completed") {
        await PatchTask(taskId, [
          { op: "replace", path: "/status", value: "completed" },
        ]);
        updateTaskInContext(taskId, { status: "completed" });
      }

      navigate("/summary", { state: { patientId } });
    } catch (error) {
      console.error("Erro ao acessar prontuário via notificação:", error);
    } finally {
      setUpdatingById((prev) => ({ ...prev, [taskId]: false }));
    }
  };

  const handleRespondQuestionnaire = useCallback(
    (task: Task) => {
      const focusReference = task.focus?.reference?.trim() ?? "";
      const questionnaireId = focusReference.startsWith("Questionnaire/")
        ? focusReference.replace("Questionnaire/", "").trim()
        : "";

      const patientTaskId = task.id?.trim() ?? "";
      const subjectReference = myPatientReference.trim();
      const requesterPractitionerRoleReference =
        task.requester?.reference?.trim() ?? "";
      const ownerOrganizationReference = task.owner?.reference?.trim() ?? "";
      const questionnaireTitle = (task.description || "Questionário").trim();
      const appointmentReference =
        getAppointmentReferenceFromQuestionnaireTask(task);

      if (
        !questionnaireId ||
        !patientTaskId ||
        !subjectReference ||
        !requesterPractitionerRoleReference ||
        !ownerOrganizationReference
      ) {
        toast.error(
          "Não foi possível abrir o questionário (dados da task incompletos).",
        );
        return;
      }

      setIsSidebarExpanded(false);
      navigate("/questionnaire-respond", {
        state: {
          questionnaireId,
          questionnaireTitle,
          subjectReference,
          patientTaskId,
          requesterPractitionerRoleReference,
          ownerOrganizationReference,
          ...(appointmentReference ? { appointmentReference } : {}),
        },
      });
    },
    [myPatientReference, navigate, setIsSidebarExpanded],
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.5,
        overflow: "hidden",
        border: "1px solid",
        borderColor: unreadCount > 0 ? "warning.main" : "divider",
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 1.5,
          py: 1.1,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: unreadCount > 0
            ? (theme) => alpha(theme.palette.warning.main, 0.06)
            : "background.paper",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {unreadCount > 0 ? (
            <NotificationsActiveOutlined
              sx={{ fontSize: 20, color: "warning.main" }}
            />
          ) : (
            <NotificationsNoneOutlined
              sx={{ fontSize: 20, color: "text.secondary" }}
            />
          )}
          <Typography variant="subtitle2" fontWeight={700}>
            Notificações
          </Typography>
          {unreadCount > 0 ? (
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{
                px: 0.75,
                py: 0.15,
                borderRadius: 99,
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.14),
                color: "warning.dark",
                fontSize: "0.6875rem",
              }}
            >
              {unreadCount}
            </Typography>
          ) : null}
        </Stack>
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          últimos 30 dias
        </Typography>
      </Stack>

      <Stack
        sx={{
          p: 1.5,
          maxHeight: 320,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "primary700.main",
            borderRadius: 99,
          },
        }}
      >
        <NotificationsPanelBody
          unreadCount={unreadCount}
          loading={false}
          accessType={access?.type}
          tasks={filteredTasks}
          updatingById={updatingById}
          showUnreadSummary={false}
          onToggleReadStatus={(task) => {
            void handleToggleReadStatus(task);
          }}
          onAccessMedicalRecord={(task) => {
            void handleAccessMedicalRecord(task);
          }}
          onRespondQuestionnaire={handleRespondQuestionnaire}
        />
      </Stack>
    </Paper>
  );
}
